<template>
  <div>
    <HomeInicio />
    
    <div v-if="paquete === 4" class="qr-scanner-container">
      <div class="scanner-header">
        <h2 class="scanner-title">
          <i class="fas fa-qrcode"></i> Escanear Código QR
        </h2>
        <p class="scanner-subtitle">Apunta con la cámara a un código QR para escanearlo</p>
      </div>

      <div v-if="!scannedData" class="scanner-body">
        <div class="scanner-controls">
          <button 
            @click="toggleCamera" 
            class="scanner-btn"
            :class="{ 'btn-stop': videoStream }"
          >
            <i :class="videoStream ? 'fas fa-stop' : 'fas fa-camera'"></i>
            {{ videoStream ? "Detener Escaneo" : "Iniciar Escaneo" }}
          </button>
          
          <button 
            v-if="hasCameraAccess"
            @click="switchCamera"
            class="scanner-btn btn-switch"
          >
            <i class="fas fa-sync-alt"></i> Cambiar Cámara
          </button>
        </div>

        <div v-show="videoStream" class="scanner-viewport">
          <video ref="videoElement" autoplay playsinline class="scanner-video" />
          <div class="scanner-overlay">
            <div class="scanner-frame"></div>
            <p class="scanner-hint">Coloca el código QR dentro del marco</p>
          </div>
          <canvas ref="canvasElement" class="scanner-canvas" />
        </div>

        <div v-if="error" class="scanner-error">
          <i class="fas fa-exclamation-triangle"></i>
          <p>{{ error }}</p>
        </div>
      </div>

      <div v-else class="scanner-result">
        <div class="result-header">
          <i class="fas fa-check-circle"></i>
          <h3>¡Código Escaneado!</h3>
        </div>
        
        <div class="result-content">
        
          
          <div v-if="isValidUrl(scannedData)" class="result-preview">
            <label>Vista previa:</label>
            <iframe :src="scannedData" class="preview-frame" />
          </div>
        </div>

        <div class="result-actions">
          <button @click="copyToClipboard" class="result-btn">
            <i class="fas fa-copy"></i> Copiar
          </button>
          <button @click="openInBrowser" class="result-btn" v-if="isValidUrl(scannedData)">
            <i class="fas fa-external-link-alt"></i> Abrir
          </button>
          <button @click="resetScanner" class="result-btn btn-primary">
            <i class="fas fa-redo"></i> Escanear otro
          </button>
        </div>
      </div>
    </div>

    <div v-else class="upgrade-required">
      <div class="upgrade-content">
        <i class="fas fa-lock"></i>
        <h3>Función Premium</h3>
        <p>El escáner QR está disponible en nuestro paquete premium</p>
        <button @click="goToUpgrade" class="upgrade-btn">
          <i class="fas fa-crown"></i> Actualizar ahora
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import jsQR from "jsqr";
import HomeInicio from "@/components/HomeInicio.vue";

export default {
  name: "QRScanner",
  components: {
    HomeInicio,
  },
  data() {
    return {
      scannedData: null,
      videoStream: null,
      scanning: false,
      error: null,
      paquete: Number(localStorage.getItem('paquete')),
      hasCameraAccess: false,
      facingMode: "environment", // Por defecto cámara trasera
    };
  },
  methods: {
    async toggleCamera() {
      if (this.videoStream) {
        this.stopCamera();
      } else {
        await this.startCamera();
      }
    },
    
    async switchCamera() {
      this.facingMode = this.facingMode === "user" ? "environment" : "user";
      this.stopCamera();
      await this.startCamera();
    },
    
    async startCamera() {
      try {
        this.error = null;
        const constraints = {
          video: { 
            facingMode: this.facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };
        
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.videoStream = stream;
        this.hasCameraAccess = true;
        this.$refs.videoElement.srcObject = stream;
        
        this.$refs.videoElement.onloadedmetadata = () => {
          this.$refs.videoElement.play();
          this.scanning = true;
          this.scanQRCode();
        };
      } catch (err) {
        this.handleCameraError(err);
      }
    },
    
    handleCameraError(error) {
      console.error("Error en la cámara:", error);
      this.error = this.getFriendlyErrorMessage(error);
      this.stopCamera();
    },
    
    getFriendlyErrorMessage(error) {
      if (error.name === 'NotAllowedError') {
        return "Se necesitan permisos para acceder a la cámara. Por favor habilita los permisos y recarga la página.";
      } else if (error.name === 'NotFoundError') {
        return "No se encontró ninguna cámara en este dispositivo.";
      } else if (error.name === 'NotReadableError') {
        return "La cámara está siendo usada por otra aplicación.";
      } else {
        return "No se pudo acceder a la cámara. Por favor intenta de nuevo.";
      }
    },
    
    stopCamera() {
      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => track.stop());
        this.videoStream = null;
      }
      this.scanning = false;
    },
    
    scanQRCode() {
      if (!this.scanning) return;

      const video = this.$refs.videoElement;
      const canvas = this.$refs.canvasElement;
      const ctx = canvas.getContext("2d");

      const scanFrame = () => {
        if (!this.scanning) return;

        if (video.readyState !== video.HAVE_ENOUGH_DATA || 
            video.videoWidth === 0 || video.videoHeight === 0) {
          requestAnimationFrame(scanFrame);
          return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const qrCode = jsQR(imageData.data, canvas.width, canvas.height, {
          inversionAttempts: "dontInvert",
        });

        if (qrCode) {
          this.scannedData = qrCode.data;
          this.stopCamera();
          this.playSuccessSound();
        } else {
          requestAnimationFrame(scanFrame);
        }
      };

      requestAnimationFrame(scanFrame);
    },
    
    playSuccessSound() {
      // Puedes implementar un sonido de éxito aquí
      console.log("QR escaneado con éxito!");
    },
    
    resetScanner() {
      this.scannedData = null;
      this.error = null;
      this.startCamera();
    },
    
    isValidUrl(string) {
      try {
        new URL(string);
        return true;
      } catch (_) {
        return false;
      }
    },
    
    copyToClipboard() {
      navigator.clipboard.writeText(this.scannedData)
        .then(() => {
          alert("Texto copiado al portapapeles!");
        })
        .catch(err => {
          console.error("Error al copiar:", err);
        });
    },
    
    openInBrowser() {
      if (this.isValidUrl(this.scannedData)) {
        window.open(this.scannedData, '_blank');
      }
    },
    
    goToUpgrade() {
      this.$router.push('/paquetes');
    }
  },
  beforeUnmount() {
    this.stopCamera();
  }
};
</script>

<style scoped>
/* Estilos base */
.qr-scanner-container {
  max-width: 800px;
  margin: 80px auto 0;
  padding: 20px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
}

.scanner-header {
  text-align: center;
  margin-bottom: 30px;
    margin-top:60px;

}

.scanner-title {
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
  color: #2d3748;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.scanner-title i {
  color: #404348;
}

.scanner-subtitle {
  color: #6b7280;
  font-size: 0.95rem;
}

/* Controles del escáner */
.scanner-controls {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.scanner-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 30px;
  background: #a390a1;
  color: white;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
}

.scanner-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

.btn-stop {
  background: #e53e3e;
}

.btn-switch {
  background: #6b7280;
}

/* Vista del escáner */
.scanner-viewport {
  position: relative;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.scanner-video {
  width: 100%;
  height: auto;
  display: block;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0,0,0,0.4);
}

.scanner-frame {
  width: 70%;
  height: 70%;
  max-width: 300px;
  border: 3px solid #fff;
  border-radius: 12px;
  position: relative;
}

.scanner-frame::before,
.scanner-frame::after {
  content: '';
  position: absolute;
  width: 30px;
  height: 30px;
  border: 3px solid #4a6fa5;
}

.scanner-frame::before {
  top: -3px;
  left: -3px;
  border-right: none;
  border-bottom: none;
}

.scanner-frame::after {
  top: -3px;
  right: -3px;
  border-left: none;
  border-bottom: none;
}

.scanner-hint {
  color: white;
  margin-top: 20px;
  text-align: center;
  font-size: 0.9rem;
  text-shadow: 0 1px 3px rgba(0,0,0,0.5);
}

.scanner-canvas {
  display: none;
}

/* Mensajes de error */
.scanner-error {
  margin-top: 20px;
  padding: 15px;
  background: #fff5f5;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  color: #e53e3e;
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: 500px;
  margin: 20px auto 0;
}

.scanner-error i {
  font-size: 1.2rem;
}

/* Resultados del escaneo */
.scanner-result {
  max-width: 600px;
  margin: 0 auto;
}

.result-header {
  text-align: center;
  margin-bottom: 25px;
}

.result-header i {
  font-size: 3rem;
  color: #38a169;
  margin-bottom: 10px;
}

.result-header h3 {
  font-size: 1.5rem;
  color: #2d3748;
}

.result-content {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 25px;
}

.result-data {
  margin-bottom: 20px;
}

.result-data label,
.result-preview label {
  display: block;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 8px;
}

.data-box {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  word-break: break-all;
  font-family: monospace;
}

.preview-frame {
  width: 100%;
  height: 300px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: white;
}

.result-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

.result-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 30px;
  background: #e2e8f0;
  color: #4a5568;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.result-btn:hover {
  background: #cbd5e0;
  transform: translateY(-2px);
}

.btn-primary {
  background: #4a6fa5;
  color: white;
}

.btn-primary:hover {
  background: #3b5b8a;
}

/* Mensaje de actualización */
.upgrade-required {
  max-width: 500px;
  margin: 100px auto 0;
  padding: 30px;
  text-align: center;
}

.upgrade-content {
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.upgrade-content i {
  font-size: 3rem;
  color: #d69e2e;
  margin-bottom: 15px;
}

.upgrade-content h3 {
  font-size: 1.5rem;
  color: #2d3748;
  margin-bottom: 10px;
}

.upgrade-content p {
  color: #6b7280;
  margin-bottom: 20px;
}

.upgrade-btn {
  padding: 12px 25px;
  border: none;
  border-radius: 30px;
  background: linear-gradient(135deg, #d69e2e, #f6ad55);
  color: white;
  font-size: 1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 10px rgba(214, 158, 46, 0.3);
}

.upgrade-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 15px rgba(214, 158, 46, 0.4);
}

/* Responsive */
@media (max-width: 768px) {
  .qr-scanner-container {
    margin-top: 70px;
    padding: 15px;
  }
  
  .scanner-title {
    font-size: 1.5rem;
  }
  
  .scanner-controls {
    flex-direction: column;
    align-items: center;
  }
  
  .scanner-btn {
    width: 100%;
    max-width: 250px;
    justify-content: center;
  }
  
  .scanner-frame {
    width: 80%;
    height: 60%;
  }
  
  .result-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .result-btn {
    width: 100%;
    max-width: 250px;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .scanner-title {
    font-size: 1.3rem;
  }
  
  .scanner-subtitle {
    font-size: 0.85rem;
  }
  
  .scanner-hint {
    font-size: 0.8rem;
  }
  
  .preview-frame {
    height: 200px;
  }
}
</style>